import React, { useState, useEffect } from "react";
import { 
  Brain, User, Mail, Lock, ChevronRight, Check, Bell, 
  ChevronDown, ChevronUp, BookOpen, Atom, Puzzle, DraftingCompass, 
  Sparkles, Loader2 
} from "lucide-react";
import { 
  signInWithEmailAndPassword, createUserWithEmailAndPassword, 
  signInWithPopup, GoogleAuthProvider, signOut, sendPasswordResetEmail 
} from "firebase/auth";
import { doc, getDoc, updateDoc, setDoc, serverTimestamp } from "firebase/firestore";

// 🟢 引用新架構
import { auth, db } from "../config/firebase";
import { STATIC_CONFIG, API_URL } from "../config/constants";
import DisclaimerModal from "../components/common/DisclaimerModal";

const LoginPage = ({ onLogin, sysConfig }) => {
  const [formEmail, setFormEmail] = useState("");
  const [formCode, setFormCode] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showOverlay, setShowOverlay] = useState(true);
  const [showDisclaimerDetail, setShowDisclaimerDetail] = useState(false);
  const [shakeDisclaimer, setShakeDisclaimer] = useState(false);
  const [loginStep, setLoginStep] = useState('initial');
  
  // 公告與須知展開狀態
  const [isExpanded, setIsExpanded] = useState(false);
  const [isNoticesExpanded, setIsNoticesExpanded] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setShowOverlay(false), 800);
    return () => clearTimeout(t);
  }, []);

  const validateAndProceed = (nextAction) => {
      if (!agreed) {
          setShakeDisclaimer(true);
          setTimeout(() => setShakeDisclaimer(false), 500);
          return;
      }
      nextAction();
  };

  const processLoginCheck = async (userObj) => {
    try {
      const userRef = doc(db, "artifacts", "chengkai-math-app", "public", "data", "registered_users", userObj.email.toLowerCase());
      const snap = await getDoc(userRef);

      if (!snap.exists() || !snap.data().name) {
        return;
      }

      const userData = snap.data();
      if (userData.status === "suspended") {
        alert("🚫 帳號異常，請聯絡老師。");
        await signOut(auth);
        setLoading(false);
        return;
      }

      if (userData.expiryDate) {
          const now = new Date();
          const expiry = userData.expiryDate.toDate(); 
          
          if (now > expiry) {
              await updateDoc(userRef, {
                  planId: "F",       
                  expiryDate: null,  
                  lastActiveAt: serverTimestamp()
              });
              alert(`💡 您的體驗期已結束，系統已自動轉為【免費會員】。\n\n每日免費 AI 解析額度調整為 1 題。\n若額度不足，將自動扣除您的帳戶點數。`);
          }
      }

      if (userData.status !== "approved") {
        alert("⏳ 您的帳號目前審核中，請等待老師開通。");
        await signOut(auth);
        setLoading(false);
        return;
      }

      updateDoc(userRef, { lastActiveAt: serverTimestamp() }).catch(console.error);
      if (agreed) setDoc(userRef, { disclaimerAgreed: true }, { merge: true }).catch(console.error);
    } catch (e) {
      console.error("Login Check Error:", e);
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setLoading(true);
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      await processLoginCheck(result.user);
    } catch (e) {
      console.error(e);
      if (e.code !== "auth/popup-closed-by-user") alert("Google 登入失敗");
      setLoading(false);
    }
  };

  const handleEmailLogin = async () => { 
      if (!formEmail || !formCode) { alert("請填寫 Email 與 密碼"); return; } 
      setLoading(true); 
      try { 
          const result = await signInWithEmailAndPassword(auth, formEmail, formCode); 
          await processLoginCheck(result.user); 
      } catch (error) { 
          alert("登入失敗：" + error.message); 
          setLoading(false); 
      } 
  };

  const handleRegisterStart = async () => { 
      if (!formEmail || !formCode) { alert("請填寫 Email 與 密碼"); return; } 
      setLoading(true);
      try { 
          await createUserWithEmailAndPassword(auth, formEmail, formCode); 
      } catch (error) { 
          alert("註冊失敗：" + error.message); 
          setLoading(false); 
      } 
  };

  const handleForgotPassword = async () => { 
      const email = prompt("請輸入您的註冊 Email：\n(注意：若是學校信箱可能會擋信，請檢查垃圾桶)");
      if (!email) return; 
      if (!email.includes("@")) { alert("❌ Email 格式不正確，請重新輸入。"); return; }

      try { 
          await sendPasswordResetEmail(auth, email);
          fetch(API_URL, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ 
                  action: "notify", type: "system", name: "系統自動回報", email: email,          
                  description: `用戶申請重設密碼：${email} (Firebase 已受理)` 
              })
          }).catch(err => console.warn("通知發送失敗:", err));
          alert("✅ 重設信已發送！\n\n1. 請檢查您的信箱 (含垃圾郵件)。\n2. 若 5 分鐘內未收到，極可能是被學校信箱擋信，請改用私人 Gmail 或聯絡老師手動重設。"); 
      } catch (e) { 
          console.error("Reset Password Error:", e);
          let errorMsg = "發送失敗，請稍後再試。";
          if (e.code === 'auth/user-not-found') errorMsg = "❌ 找不到此 Email 的註冊紀錄，請確認是否輸入錯誤。";
          else if (e.code === 'auth/invalid-email') errorMsg = "❌ Email 格式錯誤。";
          else if (e.code === 'auth/too-many-requests') errorMsg = "❌ 操作太頻繁，請稍後再試。";
          alert(errorMsg); 
      } 
  };

  const displayAnnouncement = sysConfig?.announcement || STATIC_CONFIG.announcement;
  const displayDisclaimer = sysConfig?.disclaimer || STATIC_CONFIG.disclaimer;
  const rawNotices = sysConfig?.notices;
  const noticesList = rawNotices ? (Array.isArray(rawNotices) ? rawNotices : rawNotices.split('\n')) : STATIC_CONFIG.notices;

  return (
    <div className="min-h-screen bg-[#FFF8F0] flex flex-col relative overflow-x-hidden font-sans">
      {showDisclaimerDetail && <DisclaimerModal content={displayDisclaimer} onAccept={() => { setAgreed(true); setShowDisclaimerDetail(false); }} />}
      
      <div className={`absolute inset-0 z-[999] bg-orange-500 flex items-center justify-center transition-opacity duration-700 pointer-events-none ${showOverlay ? "opacity-100" : "opacity-0"}`}>
        <Brain className="text-white animate-pulse" size={60} />
      </div>

      <div className="flex-1 flex flex-col items-center justify-start pt-6 px-4 pb-8">
          <div className="w-full max-w-4xl bg-white rounded-2xl shadow-xl shadow-orange-100/60 border border-white overflow-hidden flex flex-col md:flex-row h-auto relative">
              
              <div className="w-full md:w-5/12 p-6 flex flex-col justify-center relative bg-white md:bg-gradient-to-br md:from-orange-500 md:to-amber-500 transition-colors overflow-hidden">
                  <div className="absolute -right-12 -top-12 text-orange-50/50 md:text-white/5 rotate-12 pointer-events-none">
                     <Atom size={180} strokeWidth={1} />
                  </div>
                  <div className="absolute -right-8 -bottom-8 text-orange-50/50 md:text-white/5 -rotate-12 pointer-events-none">
                      <DraftingCompass size={120} strokeWidth={1} />
                  </div>
                  <div className="absolute right-4 top-1/2 -translate-y-1/2 text-orange-100/50 md:text-white/5 rotate-45 pointer-events-none">
                      <Puzzle size={60} strokeWidth={1.5} />
                  </div>
                  <div className="relative z-10 text-left pr-4">
                     <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-bold tracking-widest uppercase mb-3 bg-orange-50 text-orange-600 border border-orange-100 md:bg-white/20 md:text-white md:border-white/20">
                         <Sparkles size={10}/> AI Learning
                      </div>
                      <h2 className="text-2xl md:text-3xl font-black leading-tight mb-2 text-slate-800 md:text-white tracking-tight whitespace-nowrap">
                          讓學習變簡單。
                      </h2>
                      <p className="font-bold text-xs leading-relaxed text-slate-400 md:text-orange-50">
                          數理不再是難題，這裡有最強 AI 助教陪你，隨時為您解惑。
                      </p>
                  </div>
              </div>

              <div className="w-full md:w-7/12 p-6 pt-4 md:pt-8 md:p-8 bg-white relative flex flex-col justify-center items-center">
                  {loginStep === 'initial' && (
                      <div className="animate-in fade-in slide-in-from-right-4 duration-300 w-full max-w-xs flex flex-col items-center mt-2">
                          <div className="flex items-center gap-3 mb-4">
                               <div className="w-10 h-10 bg-slate-50 rounded-full flex items-center justify-center border border-slate-100">
                                  <User size={18} className="text-slate-600" />
                               </div>
                              <span className="text-sm font-black text-slate-700">學生登入 / 註冊</span>
                          </div>
                          <button 
                            onClick={() => setLoginStep('selection')}
                            className="w-full bg-slate-900 text-white py-3 rounded-xl font-bold shadow-md hover:bg-black transition-all flex items-center justify-center gap-2 group text-sm"
                          >
                             開始驗證 <ChevronRight size={16} className="group-hover:translate-x-1 transition-transform"/>
                          </button>
                          <p className="mt-4 text-[10px] text-slate-400 font-bold">
                              第一次來嗎？
                              <span className="text-orange-500 cursor-pointer hover:underline ml-1" onClick={() => setLoginStep('selection')}>
                                  建立新帳號
                              </span>
                          </p>
                      </div>
                  )}

                  {loginStep === 'selection' && (
                      <div className="animate-in fade-in slide-in-from-right-4 duration-300 w-full max-w-xs mt-2">
                         <div className="flex justify-between items-center mb-3">
                              <h3 className="text-sm font-black text-slate-800">選擇方式</h3>
                             <button onClick={() => setLoginStep('initial')} className="text-slate-400 hover:text-slate-700 text-[10px] font-bold bg-slate-100 px-3 py-1 rounded-full">返回</button>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-3 mb-2">
                              <button onClick={() => validateAndProceed(handleGoogleLogin)} disabled={loading} className={`flex flex-col items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all active:scale-95 ${!agreed ? "border-slate-100 bg-slate-50 opacity-80" : "border-slate-100 bg-white hover:border-orange-200"}`}>
                                  <img src="https://www.google.com/favicon.ico" alt="G" className="w-4 h-4" />
                                  <span className="font-bold text-slate-700 text-xs">Google</span>
                              </button>
                              
                              <button onClick={() => validateAndProceed(() => setLoginStep('emailForm'))} disabled={loading} className={`flex flex-col items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all active:scale-95 ${!agreed ? "border-slate-100 bg-slate-50 opacity-80" : "border-slate-100 bg-white hover:border-slate-800"}`}>
                                  <Mail size={16} className="text-slate-600" />
                                  <span className="font-bold text-slate-700 text-xs">Email</span>
                              </button>
                          </div>

                          <p className="text-[10px] text-red-400 font-bold text-center mb-4 bg-red-50 py-1.5 rounded-lg border border-red-100 animate-pulse">
                              ⚠️ 建議使用私人 Gmail，學校信箱易擋信
                          </p>

                          <div onClick={() => setAgreed(!agreed)} className={`flex items-center gap-2 p-2.5 rounded-lg cursor-pointer border ${agreed ? "bg-orange-50 border-orange-200" : "bg-white border-slate-100"} ${shakeDisclaimer ? "animate-bounce border-red-400" : ""}`}>
                                <div className={`w-4 h-4 rounded flex items-center justify-center border ${agreed ? "bg-orange-500 border-orange-500" : "border-slate-300 bg-white"}`}>
                                    {agreed && <Check size={10} className="text-white" strokeWidth={4} />}
                                </div>
                                <p className={`text-[10px] font-bold ${agreed ? "text-orange-800" : "text-slate-500"}`}>同意<span className="underline ml-1" onClick={(e) => { e.stopPropagation(); setShowDisclaimerDetail(true); }}>免責聲明</span></p>
                          </div>
                      </div>
                  )}

                  {loginStep === 'emailForm' && (
                      <div className="animate-in fade-in slide-in-from-right-4 duration-300 w-full max-w-xs mt-2">
                           <div className="flex justify-between items-center mb-3">
                              <h3 className="text-sm font-black text-slate-800">信箱登入</h3>
                              <button onClick={() => setLoginStep('selection')} className="text-slate-400 hover:text-slate-700 text-[10px] font-bold bg-slate-100 px-3 py-1 rounded-full">上一步</button>
                          </div>
                          <div className="space-y-2.5">
                              <div className="relative"><Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" size={14} /><input type="email" value={formEmail} onChange={(e) => setFormEmail(e.target.value)} placeholder="Email" className="w-full pl-9 pr-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-bold text-slate-700" /></div>
                              <div className="relative"><Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" size={14} /><input type="password" value={formCode} onChange={(e) => setFormCode(e.target.value)} placeholder="密碼" className="w-full pl-9 pr-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-bold text-slate-700" /></div>
                              <div className="grid grid-cols-2 gap-2 pt-1">
                                  <button onClick={handleEmailLogin} disabled={loading} className="w-full py-2.5 bg-slate-900 text-white font-bold rounded-lg text-xs">{loading ? <Loader2 className="animate-spin mx-auto"/> : "登入"}</button>
                                  <button onClick={handleRegisterStart} disabled={loading} className="w-full py-2.5 bg-white border border-slate-200 text-slate-600 font-bold rounded-lg text-xs">註冊</button>
                              </div>
                              <p className="text-center text-slate-300 text-[10px] font-bold pt-1" onClick={handleForgotPassword}>忘記密碼？</p>
                          </div>
                      </div>
                  )}
              </div>
          </div>

          <div className="w-full max-w-4xl mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex flex-col gap-2 items-start text-left">
                  <h3 className="text-orange-600 font-black flex items-center gap-1.5 text-base uppercase tracking-wider"><Bell size={12} className="fill-orange-500"/> 最新公告</h3>
                  <div className={`text-slate-600 text-xs leading-5 whitespace-pre-wrap font-medium relative overflow-hidden transition-all duration-500 w-full ${isExpanded ? "max-h-[800px]" : "max-h-[100px]"}`}>
                      {displayAnnouncement}
                      {!isExpanded && <div className="absolute bottom-0 inset-x-0 h-10 bg-gradient-to-t from-[#FFF8F0] via-[#FFF8F0]/90 to-transparent flex items-end justify-start"><button onClick={() => setIsExpanded(true)} className="text-orange-500 text-[10px] font-bold hover:underline flex items-center gap-1">展開 <ChevronDown size={10}/></button></div>}
                  </div>
                  {isExpanded && <button onClick={() => setIsExpanded(false)} className="text-orange-500 text-[10px] font-bold flex items-center gap-1"><ChevronUp size={10}/> 收起</button>}
              </div>

              <div className="flex flex-col gap-2 items-start text-left">
                  <h3 className="text-slate-800 font-black flex items-center gap-1.5 text-base uppercase tracking-wider"><BookOpen size={12} className="text-slate-400"/> 教室須知</h3>
                  <div className={`text-slate-600 text-xs leading-5 relative overflow-hidden transition-all duration-500 w-full ${isNoticesExpanded ? "max-h-[800px]" : "max-h-[100px]"}`}>
                      <ul className="space-y-2 w-full">
                        {noticesList.map((n, i) => {
                            const txt = typeof n === 'string' ? n : "";
                            return txt.trim() && (
                            <li key={i} className="flex items-start gap-2 text-slate-600 font-bold group">
                                <span className="leading-relaxed group-hover:text-slate-800 transition-colors">{txt}</span>
                            </li>
                        )})}
                      </ul>
                      {!isNoticesExpanded && <div className="absolute bottom-0 inset-x-0 h-10 bg-gradient-to-t from-[#FFF8F0] via-[#FFF8F0]/90 to-transparent flex items-end justify-start"><button onClick={() => setIsNoticesExpanded(true)} className="text-orange-500 text-[10px] font-bold hover:underline flex items-center gap-1">展開 <ChevronDown size={10}/></button></div>}
                  </div>
                  {isNoticesExpanded && <button onClick={() => setIsNoticesExpanded(false)} className="text-orange-500 text-[10px] font-bold flex items-center gap-1"><ChevronUp size={10}/> 收起</button>}
              </div>
          </div>
          <div className="mt-8 text-slate-300 text-[10px] font-bold text-center">Copyright © 2025 大帥的數理AI教室.</div>
      </div>
    </div>
  );
};

export default LoginPage;
